import { useMemo, useState } from 'react';
import { useEmergencyStore } from '@/stores/emergency-store';
import type { ClientLive } from '@/stores/emergency-store';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Search, MapPin, Phone, Clock, Circle, BatteryLow, Battery, BatteryFull, Smartphone } from 'lucide-react';

function getClientStatusLabel(status: ClientLive['status']) {
  switch (status) {
    case 'ONLINE':
      return 'En línea';
    case 'BACKGROUND':
      return 'Segundo plano';
    case 'OFFLINE':
      return 'Desconectado';
  }
}

function getClientStatusDot(status: ClientLive['status']) {
  switch (status) {
    case 'ONLINE':
      return 'fill-violet-400 text-violet-400';
    case 'BACKGROUND':
      return 'fill-cyan-400 text-cyan-400';
    case 'OFFLINE':
      return 'fill-slate-400 text-slate-400';
  }
}

function BatteryIcon({ level }: { level: number | null }) {
  if (level == null) return null;
  if (level <= 15) return <BatteryLow className="w-3 h-3 text-red-400" />;
  if (level <= 50) return <Battery className="w-3 h-3 text-amber-400" />;
  return <BatteryFull className="w-3 h-3 text-green-400" />;
}

export function ClientList() {
  const { clients, clientsLoading, selectClient } = useEmergencyStore();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.phone ?? '').toLowerCase().includes(q),
    );
  }, [clients, query]);

  const onlineCount = clients.filter((c) => c.status === 'ONLINE').length;
  const bgCount = clients.filter((c) => c.status === 'BACKGROUND').length;
  const offlineCount = clients.filter((c) => c.status === 'OFFLINE').length;

  if (clientsLoading && clients.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400">
          <div className="w-5 h-5 border-2 border-gray-400 dark:border-slate-400 border-t-transparent rounded-full animate-spin" />
          <span>Cargando clientes...</span>
        </div>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <div className="w-16 h-16 bg-gray-200 dark:bg-slate-700 rounded-full flex items-center justify-center mb-4">
          <Smartphone className="w-8 h-8 text-gray-500 dark:text-slate-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Sin clientes activos</h3>
        <p className="text-gray-500 dark:text-slate-400 text-sm">
          Los clientes aparecerán aquí cuando compartan su ubicación
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Search */}
      <div className="p-3 border-b border-gray-200 dark:border-slate-800">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar cliente por nombre o teléfono…"
            className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-800 flex gap-4 text-xs">
        <span className="flex items-center gap-1.5 text-gray-700 dark:text-slate-300">
          <Circle className="w-2.5 h-2.5 fill-violet-400 text-violet-400" /> {onlineCount} en línea
        </span>
        <span className="flex items-center gap-1.5 text-gray-700 dark:text-slate-300">
          <Circle className="w-2.5 h-2.5 fill-cyan-400 text-cyan-400" /> {bgCount} en bg
        </span>
        <span className="flex items-center gap-1.5 text-gray-700 dark:text-slate-300">
          <Circle className="w-2.5 h-2.5 fill-slate-400 text-slate-400" /> {offlineCount} offline
        </span>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500 dark:text-slate-400">
            No hay coincidencias para “{query}”.
          </div>
        ) : (
          filtered.map((client) => (
            <button
              key={client.id}
              onClick={() => selectClient(client)}
              className="w-full p-4 border-b border-gray-200 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors text-left"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-blue-500 dark:text-blue-300" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900 dark:text-white truncate">{client.name}</span>
                    <Circle className={`w-2.5 h-2.5 ${getClientStatusDot(client.status)}`} />
                    <span className="text-xs text-gray-500 dark:text-slate-400 whitespace-nowrap">
                      {getClientStatusLabel(client.status)}
                    </span>
                  </div>

                  {client.phone && (
                    <a
                      href={`tel:${client.phone}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 text-blue-400 text-xs hover:underline mb-1"
                    >
                      <Phone className="w-3 h-3" />
                      {client.phone}
                    </a>
                  )}

                  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDistanceToNow(new Date(client.last_update), {
                        addSuffix: true,
                        locale: es,
                      })}
                    </span>
                    {client.battery_level != null && (
                      <span className="flex items-center gap-1">
                        <BatteryIcon level={client.battery_level} />
                        {client.battery_level}%{client.is_charging ? ' ⚡' : ''}
                      </span>
                    )}
                  </div>
                </div>

                <span
                  className="p-2 text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  title="Centrar en el mapa"
                >
                  <MapPin className="w-4 h-4" />
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
